import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';

import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { ApiServiceService } from '../api-service.service';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-group-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxPaginationModule,
    DatePipe,
    MatIcon,
    MatFormField,
    MatError,
    MatLabel,
    MatInput,
    MatTooltip,
    CommonSearchComponent,
    CommonPaginationComponent,
  ],
  templateUrl: './group-management.component.html',
  styleUrl: './group-management.component.scss',
})
export class GroupManagementComponent implements OnInit {
  listOfRole = [];
  totalLength = 0;
  size = 10;
  page = 1;
  search = '';

  userPermission = [];
  roleType = '';

  roleGroupForm: FormGroup = this.formBuilder.group({});
  addRoleGroupForm: FormGroup = this.formBuilder.group({});

  addNewRoleGroup = false;
  editRoleGroup = false;
  selectedRoleId = '';

  roleGroupCreate = false;
  roleGroupUpdate = false;
  roleGroupDelete = false;

  roleGroupFormView = false;

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.setRoleGroupForm();
    this.checkPermission();
    if (this.roleGroupFormView) {
      this.fetchRoleGroupList();
    }
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.roleType = this.apiMicroservice.decryptData(
      sessionStorage.getItem('roleType') || ''
    );
    if (this.roleType === 'SA') {
      this.roleGroupFormView = true;
      this.roleGroupCreate = true;
      this.roleGroupUpdate = true;
      this.roleGroupDelete = true;
    } else {
      this.navigateBack();
    }
  }

  setRoleGroupForm() {
    this.roleGroupForm = this.formBuilder.group({
      roleGroupListSearch: new FormControl(''),
    });

    this.addRoleGroupForm = this.formBuilder.group({
      groupName: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.nonNumericNoSpecialChars,
      ]),
    });
  }

  fetchRoleGroupList() {
    this.spinner.show();
    this.listOfRole = [];
    this.apiMicroservice
      .getRoleGroup(this.page, this.size, this.search)
      .subscribe({
        next: (res) => {
          if (res && res.status) {
            this.listOfRole = res.Data;
            this.totalLength = res.totalItems;
            this.apiMicroservice.openSnackbar(res.message);
          } else {
            this.apiMicroservice.openSnackbar(
              res?.message || 'Something went wrong'
            );
          }
          this.spinner.hide();
        },
        error: (err) => {
          console.error('API Error:', err);
          this.apiMicroservice.openSnackbar('Failed to fetch role groups.');
          this.spinner.hide();
        },
      });
  }

  save() {
    if (this.addRoleGroupForm.invalid) {
      SharedValidators.markAllAsTouched(this.addRoleGroupForm);
    } else {
      this.saveRoleGroup();
    }
  }

  saveRoleGroup() {
    this.spinner.show();
    const payload = this.addRoleGroupForm.value;
    payload.updatedAt = Date.now();
    if (this.editRoleGroup && this.selectedRoleId) {
      // Update existing Role Group
      this.apiMicroservice
        .updateRoleGroup(this.selectedRoleId, payload)
        .subscribe({
          next: (res) => {
            this.cancelFunc();
            if (res.status) {
              this.apiMicroservice.openSnackbar(res.message);
              // this.resetForm();
              this.fetchRoleGroupList();
            } else {
              this.apiMicroservice.openSnackbar(res.message);
              this.spinner.hide();
            }
          },
          error: (error) => {
            this.apiMicroservice.openSnackbar(
              error.error?.message || 'Something went wrong. Please try again.'
            );
            this.spinner.hide();
          },
        });
    } else {
      // Add new Role Group
      this.apiMicroservice.addRoleGroup(payload).subscribe({
        next: (res) => {
          this.cancelFunc();
          if (res.status) {
            this.apiMicroservice.openSnackbar(res.message);
            // this.resetForm();
            this.fetchRoleGroupList();
          } else {
            this.apiMicroservice.openSnackbar(res.message);
            this.spinner.hide();
          }
        },
        error: (error) => {
          this.apiMicroservice.openSnackbar(
            error.error?.message || 'Something went wrong. Please try again.'
          );
          this.spinner.hide();
        },
      });
    }
  }

  prePopulation(roleId) {
    if (roleId) {
      this.spinner.show();
      this.addNewRoleGroup = true;
      this.editRoleGroup = true;
      this.selectedRoleId = roleId;
      this.apiMicroservice.getRoleGroupById(roleId).subscribe((res) => {
        if (res.status) {
          this.spinner.hide();
          this.addRoleGroupForm.patchValue(res.data[0]);
        }
      });
    } else {
      this.setRoleGroupForm();
    }
  }

  onPageChange(event: number) {
    this.page = event;
    this.fetchRoleGroupList();
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    this.fetchRoleGroupList();
  }

  onSearch(event: string) {
    this.page = 1;
    this.search = event;
    this.roleGroupForm
      .get('roleGroupListSearch')
      .setValue(this.search ? this.search : '');
    this.fetchRoleGroupList();
  }

  deleteRoleGroup(id) {
    if (confirm('Are you sure?')) {
      this.spinner.show();
      this.apiMicroservice.deleteRoleGroup(id).subscribe({
        next: (res) => {
          if (res.status) {
            this.ngOnInit();
          }
        },
        error: (err) => {
          console.error('API Error:', err);
          this.apiMicroservice.openSnackbar(err.error.message);
          this.spinner.hide();
        },
      });
    }
  }

  addNewRoleGroupFunc() {
    this.addNewRoleGroup = true;
    this.editRoleGroup = false;
    this.addRoleGroupForm.reset();
  }

  cancelFunc() {
    this.addNewRoleGroup = false;
    this.selectedRoleId = '';
    this.fetchRoleGroupList();
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
